import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Play, Square, MapPin, Clock, Timer, Users, ListChecks, X } from "lucide-react";

function fmtHM(iso) {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
function fmtDT(iso) {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function fmtDuration(ms) {
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
    return `${m}:${sec.toString().padStart(2, "0")}`;
}

async function getPosition() {
    if (!navigator.geolocation) return { lat: null, lng: null };
    return new Promise((resolve) => {
        const timer = setTimeout(() => resolve({ lat: null, lng: null }), 6000);
        navigator.geolocation.getCurrentPosition(
            (pos) => { clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
            () => { clearTimeout(timer); resolve({ lat: null, lng: null }); },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 60000 },
        );
    });
}

export default function ClockInOutPanel({ job, canControl, onUpdate }) {
    const { user } = useAuth();
    const [busy, setBusy] = useState(false);
    const [now, setNow] = useState(Date.now());
    // Clock-out confirmation modal state. Opens whenever the operative taps
    // "Clock out" so they can optionally leave a hand-over note. The modal
    // itself is responsible for calling the server once the checklist gate
    // has passed; this component just controls visibility.
    const [showClockOutModal, setShowClockOutModal] = useState(false);
    const [handoverNote, setHandoverNote] = useState("");

    // Prefer the new per-person time_entries list. Fall back to top-level
    // fields for jobs clocked into before the multi-person upgrade.
    const entries = Array.isArray(job.time_entries) ? job.time_entries : [];
    const openEntries = entries.filter(e => !e.clocked_out_at);
    const anyoneOnSite = openEntries.length > 0 || (job.clocked_in_at && !job.clocked_out_at);
    const myOpenEntry = openEntries.find(e => e.user_id === user?.id) || null;

    // Total cleaning hours on this job — sum of every entry (closed + open,
    // with open ones ticking live). Falls back to the legacy top-level
    // times for jobs clocked into before the multi-person upgrade. Everyone
    // who can view the job sees this total, so a manager reviewing pay or
    // a client asking "how long were you there?" gets the same number.
    let totalMs = 0;
    if (entries.length > 0) {
        for (const e of entries) {
            if (!e.clocked_in_at) continue;
            const start = new Date(e.clocked_in_at).getTime();
            const end = e.clocked_out_at ? new Date(e.clocked_out_at).getTime() : now;
            totalMs += Math.max(0, end - start);
        }
    } else if (job.clocked_in_at) {
        const start = new Date(job.clocked_in_at).getTime();
        const end = job.clocked_out_at ? new Date(job.clocked_out_at).getTime() : now;
        totalMs = Math.max(0, end - start);
    }
    const totalHours = totalMs / 3_600_000;
    const closedTotalIsFinal = entries.length > 0
        ? (entries.every(e => e.clocked_out_at) && entries.length > 0)
        : Boolean(job.clocked_out_at);

    // Legacy display (only used when entries is empty)
    const legacyInAt = entries.length === 0 && job.clocked_in_at ? new Date(job.clocked_in_at) : null;
    const legacyOutAt = entries.length === 0 && job.clocked_out_at ? new Date(job.clocked_out_at) : null;
    const legacyActive = legacyInAt && !legacyOutAt;

    useEffect(() => {
        if (!anyoneOnSite) return;
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, [anyoneOnSite]);

    const clockIn = async () => {
        setBusy(true);
        try {
            const pos = await getPosition();
            const { data } = await api.post(`/jobs/${job.id}/clock-in`, pos);
            onUpdate(data);
            toast.success("Clocked in — good luck!");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const clockOut = async () => {
        setBusy(true);
        try {
            const pos = await getPosition();
            const { data } = await api.post(`/jobs/${job.id}/clock-out`, {
                ...pos,
                handover_note: handoverNote.trim() || null,
            });
            onUpdate(data);
            toast.success("Nicely done — clocked out.");
            setShowClockOutModal(false);
            setHandoverNote("");
        } catch (e) {
            toast.error(formatApiError(e));
        }
        finally { setBusy(false); }
    };

    // How many checklist items are still un-ticked? Mandatory gate before
    // an operative can finish a job — managers bypass this (and keep the
    // dedicated /force-clock-out route for other staff).
    const checklist = Array.isArray(job.checklist) ? job.checklist : [];
    const done = new Set(Array.isArray(job.checklist_done) ? job.checklist_done : []);
    const pendingItems = checklist.filter(item => !done.has(item));
    const checklistBlocking = pendingItems.length > 0 && user?.role !== "manager";

    const forceClockOut = async (entry) => {
        const name = entry.user_name || "them";
        const ok = window.confirm(
            `Clock ${name} out of this job right now?\n\n` +
            `Use this if they've forgotten to end their shift. The clock will close at the current time and their total hours will be finalised.`
        );
        if (!ok) return;
        setBusy(true);
        try {
            // Location intentionally omitted — the manager isn't the one on
            // site, so we don't record a bogus GPS point for the operative.
            const { data } = await api.post(`/jobs/${job.id}/force-clock-out`, {
                user_id: entry.user_id,
                lat: null,
                lng: null,
            });
            onUpdate(data);
            toast.success(`${name.split(" ")[0]} is now clocked out.`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const isManager = user?.role === "manager";

    const mapLink = (lat, lng) =>
        (lat != null && lng != null) ? `https://www.google.com/maps?q=${lat},${lng}` : null;

    // Distance (in metres) between two WGS-84 points. Pure JS so we can
    // flag clock-ins that look suspiciously far from the site address
    // without a network call. ~200 m is a comfortable urban buffer.
    const OFF_SITE_THRESHOLD_METRES = 200;
    const haversineMetres = (lat1, lng1, lat2, lng2) => {
        if ([lat1, lng1, lat2, lng2].some(v => v == null || Number.isNaN(Number(v)))) return null;
        const R = 6371000;
        const toRad = (x) => (x * Math.PI) / 180;
        const dLat = toRad(lat2 - lat1);
        const dLng = toRad(lng2 - lng1);
        const a = Math.sin(dLat / 2) ** 2
                + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(a));
    };
    const offSiteBadge = (lat, lng) => {
        if (job.address_lat == null || job.address_lng == null) return null;
        const d = haversineMetres(lat, lng, job.address_lat, job.address_lng);
        if (d == null || d <= OFF_SITE_THRESHOLD_METRES) return null;
        return d >= 1000 ? `${(d / 1000).toFixed(1)}km away` : `${Math.round(d)}m away`;
    };

    // Nothing to show if the job has never been clocked into AND the current
    // user can't clock in.
    if (entries.length === 0 && !legacyInAt && !canControl) return null;

    return (
        <section className="card-flat p-6" data-testid="clock-panel">
            <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                    <Timer size={16} /> Time on site
                </h3>
                {anyoneOnSite && (
                    <span className="pill status-in_progress inline-flex items-center gap-1" data-testid="clock-live-indicator">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-60"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500"></span>
                        </span>
                        {openEntries.length > 1 ? `${openEntries.length} on site` : "On site"}
                    </span>
                )}
            </div>

            {/* Total cleaning hours — visible to everyone who can see the job.
                Ticks live while anyone is still on site, freezes once
                everyone has clocked out. Sum across every person's session. */}
            {totalMs > 0 && (
                <div
                    data-testid="clock-total-hours"
                    className="mb-4 p-4 rounded-xl flex items-center justify-between gap-3"
                    style={{ background: closedTotalIsFinal ? "var(--pine-light)" : "#FFF7E6" }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pine)] font-semibold">
                            Total cleaning hours{!closedTotalIsFinal ? " (live)" : ""}
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] mt-0.5">
                            {entries.length > 1
                                ? `Combined across ${entries.length} people`
                                : (closedTotalIsFinal ? "Session finished" : "Ticking while on site")}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="font-display text-2xl sm:text-3xl font-bold text-[var(--pine)] tabular-nums" data-testid="clock-total-hours-value">
                            {totalHours >= 1
                                ? `${totalHours.toFixed(totalHours >= 10 ? 1 : 2)}h`
                                : fmtDuration(totalMs)}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold mt-0.5">
                            {fmtDuration(totalMs)}
                        </div>
                    </div>
                </div>
            )}

            {/* Multi-person mode: list everyone who's clocked in / out on this job */}
            {entries.length > 0 && (
                <div className="space-y-2 mb-4" data-testid="clock-entries-list">
                    {entries
                        .slice()
                        .sort((a, b) => (a.clocked_in_at || "").localeCompare(b.clocked_in_at || ""))
                        .map((e, idx) => {
                            const isOpen = !e.clocked_out_at;
                            const startedAt = new Date(e.clocked_in_at);
                            const endedAt = e.clocked_out_at ? new Date(e.clocked_out_at) : null;
                            const durationMs = endedAt ? (endedAt - startedAt) : (now - startedAt.getTime());
                            const canForceOut = isOpen && isManager && e.user_id !== user?.id;
                            return (
                                <div
                                    key={`${e.user_id}-${idx}`}
                                    data-testid={`clock-entry-${e.user_id}-${idx}`}
                                    className={`flex flex-wrap items-center justify-between gap-3 p-3 rounded-xl border ${
                                        isOpen ? "border-[var(--pine)]/40 bg-[var(--pine-light)]" : "border-[var(--border-soft)] bg-white"
                                    }`}>
                                    <div className="flex items-center gap-2 min-w-0">
                                        {e.user_color ? (
                                            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: e.user_color }} />
                                        ) : (
                                            <Users size={14} className="text-[var(--text-muted)]" />
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold truncate">{e.user_name || "Someone"}</div>
                                            <div className="text-[11px] text-[var(--text-muted)]">
                                                In {fmtHM(e.clocked_in_at)}
                                                {endedAt && ` · Out ${fmtHM(e.clocked_out_at)}`}
                                                {e.forced_out_by_name && (
                                                    <span className="text-[var(--terracotta)]"> · closed by {e.forced_out_by_name.split(" ")[0]}</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 ml-auto flex-wrap justify-end">
                                        <span className={`font-display font-semibold tabular-nums ${isOpen ? "text-[var(--pine)]" : "text-[var(--text-primary)]"}`}>
                                            {fmtDuration(durationMs)}
                                        </span>
                                        {mapLink(e.clock_in_lat, e.clock_in_lng) && (
                                            <a href={mapLink(e.clock_in_lat, e.clock_in_lng)} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-[var(--pine)] hover:underline"
                                                data-testid={`entry-in-map-${e.user_id}`}>
                                                <MapPin size={12} /> In
                                            </a>
                                        )}
                                        {mapLink(e.clock_out_lat, e.clock_out_lng) && (
                                            <a href={mapLink(e.clock_out_lat, e.clock_out_lng)} target="_blank" rel="noreferrer"
                                                className="inline-flex items-center gap-1 text-xs text-[var(--pine)] hover:underline"
                                                data-testid={`entry-out-map-${e.user_id}`}>
                                                <MapPin size={12} /> Out
                                            </a>
                                        )}
                                        {offSiteBadge(e.clock_in_lat, e.clock_in_lng, "clock-in") && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--terracotta)] text-[var(--terracotta)] bg-[#FFF7E6]"
                                                data-testid={`entry-offsite-in-${e.user_id}`}
                                                title={`Clocked IN ${offSiteBadge(e.clock_in_lat, e.clock_in_lng)} from the site address`}>
                                                <MapPin size={10} /> In {offSiteBadge(e.clock_in_lat, e.clock_in_lng)}
                                            </span>
                                        )}
                                        {offSiteBadge(e.clock_out_lat, e.clock_out_lng, "clock-out") && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--terracotta)] text-[var(--terracotta)] bg-[#FFF7E6]"
                                                data-testid={`entry-offsite-out-${e.user_id}`}
                                                title={`Clocked OUT ${offSiteBadge(e.clock_out_lat, e.clock_out_lng)} from the site address`}>
                                                <MapPin size={10} /> Out {offSiteBadge(e.clock_out_lat, e.clock_out_lng)}
                                            </span>
                                        )}
                                        {canForceOut && (
                                            <button
                                                data-testid={`force-clock-out-${e.user_id}`}
                                                onClick={() => forceClockOut(e)}
                                                disabled={busy}
                                                title={`Manually clock ${e.user_name || "them"} out of this job`}
                                                className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border border-[var(--terracotta)] text-[var(--terracotta)] hover:bg-[var(--terracotta)] hover:text-white transition-colors">
                                                <Square size={11} /> Clock out
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Legacy display (only kicks in when entries is empty) */}
            {entries.length === 0 && legacyActive && (
                <div className="mb-4 p-4 rounded-xl text-center" style={{ background: "var(--pine-light)" }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pine)] font-semibold">Live duration</div>
                    <div className="font-display text-3xl font-bold mt-1" data-testid="live-duration">
                        {fmtDuration(now - legacyInAt.getTime())}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] mt-1">
                        Started at {fmtHM(legacyInAt.toISOString())}
                    </div>
                </div>
            )}
            {entries.length === 0 && !legacyActive && legacyInAt && legacyOutAt && (
                <div className="grid grid-cols-3 gap-3 mb-4">
                    <TimeStat label="Started" value={fmtHM(legacyInAt.toISOString())} />
                    <TimeStat label="Finished" value={fmtHM(legacyOutAt.toISOString())} />
                    <TimeStat label="Duration" value={fmtDuration(legacyOutAt - legacyInAt)} highlight />
                </div>
            )}
            {entries.length === 0 && (legacyInAt || legacyOutAt) && (
                <div className="space-y-2 text-sm mb-4">
                    {legacyInAt && (
                        <div className="flex items-center justify-between p-3 rounded-xl border border-[var(--border-soft)]">
                            <div className="flex items-center gap-2">
                                <Play size={14} className="text-[var(--success)]" />
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Clocked in</div>
                                    <div>{fmtDT(legacyInAt.toISOString())}</div>
                                </div>
                            </div>
                            {mapLink(job.clock_in_lat, job.clock_in_lng) && (
                                <div className="flex items-center gap-2">
                                    {offSiteBadge(job.clock_in_lat, job.clock_in_lng) && (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--terracotta)] text-[var(--terracotta)] bg-[#FFF7E6]">
                                            <MapPin size={10} /> {offSiteBadge(job.clock_in_lat, job.clock_in_lng)}
                                        </span>
                                    )}
                                    <a href={mapLink(job.clock_in_lat, job.clock_in_lng)} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-[var(--pine)] hover:underline">
                                        <MapPin size={12} /> Map
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                    {legacyOutAt && (
                        <div className="flex items-center justify-between p-3 rounded-xl border border-[var(--border-soft)]">
                            <div className="flex items-center gap-2">
                                <Square size={14} className="text-[var(--text-secondary)]" />
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Clocked out</div>
                                    <div>{fmtDT(legacyOutAt.toISOString())}</div>
                                </div>
                            </div>
                            {mapLink(job.clock_out_lat, job.clock_out_lng) && (
                                <div className="flex items-center gap-2">
                                    {offSiteBadge(job.clock_out_lat, job.clock_out_lng) && (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-[var(--terracotta)] text-[var(--terracotta)] bg-[#FFF7E6]">
                                            <MapPin size={10} /> {offSiteBadge(job.clock_out_lat, job.clock_out_lng)}
                                        </span>
                                    )}
                                    <a href={mapLink(job.clock_out_lat, job.clock_out_lng)} target="_blank" rel="noreferrer"
                                        className="inline-flex items-center gap-1 text-xs text-[var(--pine)] hover:underline">
                                        <MapPin size={12} /> Map
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Per-user controls: show ONE clock-in OR clock-out button
                depending on whether the CURRENT user has an open entry. */}
            {canControl && !myOpenEntry && (
                <button data-testid="clock-in-btn" onClick={clockIn} disabled={busy}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2 min-h-[52px] text-base">
                    <Play size={18} /> {busy ? "Clocking in…" : (anyoneOnSite ? "Clock in — I'm on site too" : "Clock in — I'm on site")}
                </button>
            )}
            {canControl && myOpenEntry && (
                <>
                    {checklistBlocking && (
                        <div
                            data-testid="checklist-gate-warning"
                            className="mb-3 p-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
                            <ListChecks size={16} className="shrink-0 mt-0.5" />
                            <div>
                                <div className="font-semibold">
                                    {pendingItems.length === 1
                                        ? "1 checklist item still to tick"
                                        : `${pendingItems.length} checklist items still to tick`}
                                </div>
                                <div className="text-xs mt-0.5">Finish the list below before you clock out.</div>
                            </div>
                        </div>
                    )}
                    <button
                        data-testid="clock-out-btn"
                        onClick={() => { setHandoverNote(""); setShowClockOutModal(true); }}
                        disabled={busy || checklistBlocking}
                        className="btn-accent w-full inline-flex items-center justify-center gap-2 min-h-[52px] text-base disabled:opacity-50 disabled:cursor-not-allowed">
                        <Square size={18} /> {busy ? "Finishing…" : "Clock out — I'm done"}
                    </button>
                </>
            )}
            {canControl && !myOpenEntry && !anyoneOnSite && (legacyOutAt || entries.some(e => e.clocked_out_at)) && (
                <div className="text-xs text-center text-[var(--text-muted)] flex items-center justify-center gap-1 mt-2">
                    <Clock size={12} /> You can clock in again if you're back on this job.
                </div>
            )}

            {/* Clock-out confirmation / hand-over modal. Mandatory for the
                operative: can't open until every checklist item is ticked.
                Note itself is optional — a plain Confirm clocks them out
                without saving anything to handover_notes. */}
            {showClockOutModal && (
                <div
                    data-testid="clock-out-modal"
                    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    onClick={() => !busy && setShowClockOutModal(false)}>
                    <div
                        className="bg-[var(--surface)] rounded-2xl shadow-2xl max-w-md w-full p-6 relative"
                        onClick={(e) => e.stopPropagation()}>
                        <button
                            data-testid="clock-out-modal-close"
                            onClick={() => !busy && setShowClockOutModal(false)}
                            className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-[var(--border-soft)]"
                            aria-label="Cancel">
                            <X size={16} />
                        </button>
                        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                            <Square size={16} /> Finish up on this job
                        </h3>
                        <p className="text-sm text-[var(--text-secondary)] mt-1">
                            All done? Clocking out will finalise your time on this job.
                        </p>
                        <label className="block mt-4 text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">
                            Hand-over note <span className="normal-case tracking-normal text-[var(--text-muted)]">· optional</span>
                        </label>
                        <textarea
                            data-testid="clock-out-handover-input"
                            value={handoverNote}
                            onChange={(e) => setHandoverNote(e.target.value.slice(0, 500))}
                            rows={4}
                            placeholder="Anything the next team or your manager should know? Leave blank if all's well."
                            className="input-field mt-1 resize-none"
                            autoFocus
                        />
                        <div className="text-[11px] text-[var(--text-muted)] mt-1 text-right">
                            {handoverNote.length}/500
                        </div>
                        <div className="flex gap-2 mt-4">
                            <button
                                data-testid="clock-out-cancel-btn"
                                onClick={() => setShowClockOutModal(false)}
                                disabled={busy}
                                className="btn-ghost flex-1">
                                Cancel
                            </button>
                            <button
                                data-testid="clock-out-confirm-btn"
                                onClick={clockOut}
                                disabled={busy}
                                className="btn-accent flex-1 inline-flex items-center justify-center gap-2">
                                <Square size={14} /> {busy ? "Finishing…" : "Confirm & clock out"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}

function TimeStat({ label, value, highlight }) {
    return (
        <div className={`rounded-xl p-3 text-center ${highlight ? "" : "border border-[var(--border-soft)]"}`}
            style={highlight ? { background: "var(--pine-light)" } : undefined}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-muted)] font-semibold">{label}</div>
            <div className={`font-display font-bold mt-1 ${highlight ? "text-xl text-[var(--pine)]" : "text-lg"}`}>{value}</div>
        </div>
    );
}
